import { useMemo } from "react";
import { CheckCircle2, CircleDot, Circle, Home, Flag } from "lucide-react";

/**
 * Horizontal COE-countdown timeline for a transaction.
 * Renders every deadline (+ acceptance and close-of-escrow anchors) on a single
 * scrollable track. Past milestones fade back, today is highlighted, upcoming
 * ones stay crisp. Meant to sit at the top of TransactionDetail.
 */
export default function TransactionTimeline({ tx }) {
  const items = useMemo(() => {
    const arr = [];
    if (tx.acceptance_date) {
      arr.push({
        id: "acc",
        label: "Acceptance",
        date: tx.acceptance_date,
        anchor: true,
        severity: "normal",
        status: "met",
      });
    }
    for (const d of tx.deadlines || []) {
      arr.push({
        id: d.id,
        label: d.label,
        date: d.due_date,
        anchor: false,
        severity: d.severity,
        status: d.status,
        basis: d.basis,
      });
    }
    if (tx.close_of_escrow_date) {
      arr.push({
        id: "coe",
        label: "Close of escrow",
        date: tx.close_of_escrow_date,
        anchor: true,
        severity: "high",
        status: tx.status === "closed" ? "met" : "pending",
      });
    }
    // Sort chronologically, stable
    arr.sort((a, b) => (a.date > b.date ? 1 : a.date < b.date ? -1 : 0));
    return arr;
  }, [tx]);

  if (items.length === 0) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysUntil = (d) => Math.round((new Date(d) - today) / 86400000);

  // Find the closest upcoming (or today's) milestone to auto-scroll into view
  const focusIdx = items.findIndex((it) => daysUntil(it.date) >= 0);

  const coe = items.find((i) => i.id === "coe");
  const coeDays = coe ? daysUntil(coe.date) : null;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 mb-6" data-testid="tx-timeline">
      <div className="flex items-baseline justify-between mb-5">
        <div>
          <div className="flex items-center gap-2">
            <Flag className="h-4 w-4 text-slate-500" />
            <h2 className="font-heading text-sm font-bold tracking-tight text-slate-900">Deal room timeline</h2>
          </div>
          <p className="text-xs text-slate-500 mt-1">Every past and upcoming milestone in one track.</p>
        </div>
        {coe && coeDays !== null && (
          <div className="text-right" data-testid="tx-coe-countdown">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Close of escrow</div>
            <div className="font-heading text-2xl font-bold tabular-nums tracking-tighter text-slate-900">
              {coeDays > 0 && <span className="text-emerald-700">{coeDays}d out</span>}
              {coeDays === 0 && <span className="text-amber-700">Today</span>}
              {coeDays < 0 && <span className="text-slate-500">{Math.abs(coeDays)}d ago</span>}
            </div>
          </div>
        )}
      </div>

      <div className="relative">
        <div className="overflow-x-auto pb-2" data-testid="tx-timeline-track">
          <div className="relative min-w-full inline-flex items-start gap-0" style={{ minWidth: `${items.length * 148}px` }}>
            {/* Track line */}
            <div className="absolute left-8 right-8 top-[22px] h-[2px] bg-slate-200" aria-hidden />
            {items.map((it, idx) => {
              const days = daysUntil(it.date);
              const isPast = days < 0 || it.status === "met" || it.status === "waived";
              const isToday = days === 0 && !isPast;
              const isNext = idx === focusIdx && !isToday;
              const missed = days < 0 && it.status !== "met" && it.status !== "waived" && !it.anchor;

              const Icon = isPast ? CheckCircle2 : isToday ? CircleDot : Circle;
              const iconColor = missed
                ? "text-rose-600"
                : isPast
                ? "text-emerald-600"
                : isToday
                ? "text-amber-600"
                : isNext
                ? "text-slate-900"
                : "text-slate-400";
              const labelColor = missed
                ? "text-rose-900"
                : isPast
                ? "text-slate-500"
                : isToday
                ? "text-amber-900 font-semibold"
                : isNext
                ? "text-slate-900 font-semibold"
                : "text-slate-700";

              return (
                <div
                  key={it.id}
                  className="relative flex-shrink-0 w-[148px] flex flex-col items-center px-2 group"
                  data-testid={`timeline-item-${it.id}`}
                >
                  <div className={`relative z-10 h-11 w-11 rounded-full bg-white ${it.anchor ? "border-2 border-slate-900" : "border-2 border-slate-200"} flex items-center justify-center`}>
                    {it.anchor && it.id === "coe" && !isPast ? (
                      <Home className={`h-5 w-5 ${iconColor}`} />
                    ) : (
                      <Icon className={`h-5 w-5 ${iconColor}`} />
                    )}
                  </div>
                  {it.basis && (
                    <div className="pointer-events-none absolute z-20 top-14 left-1/2 -translate-x-1/2 w-56 opacity-0 group-hover:opacity-100 transition-opacity duration-150 rounded-md bg-slate-900 text-white text-[11px] leading-snug px-3 py-2 shadow-lg" data-testid={`timeline-tooltip-${it.id}`}>
                      <div className="font-semibold mb-0.5">{it.label}</div>
                      <div className="text-slate-300">{it.basis}</div>
                      <div className="absolute -top-1 left-1/2 -translate-x-1/2 h-2 w-2 rotate-45 bg-slate-900" />
                    </div>
                  )}
                  <div className={`mt-2 text-[11px] text-center leading-tight ${labelColor}`}>
                    {it.label}
                  </div>
                  <div className="mt-1 text-[10px] tabular-nums text-slate-400">
                    {formatDate(it.date)}
                  </div>
                  <div className="mt-0.5 text-[9px] uppercase tracking-widest font-semibold">
                    {isPast && <span className="text-emerald-700">done</span>}
                    {isToday && <span className="text-amber-700">today</span>}
                    {missed && <span className="text-rose-700">missed</span>}
                    {!isPast && !isToday && !missed && (
                      <span className="text-slate-400">
                        in {days}d
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
